import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  X,
  FileText,
  Brain,
  CheckCircle,
  AlertCircle,
  Clock,
  User,
  Database,
  Search,
  Link,
  Target,
  Edit3,
  BarChart3,
  Lock,
  Unlock,
  Shield
} from 'lucide-react';

import api from '../../api';
import toast from 'react-hot-toast';

interface StepAuditPopupProps {
  isOpen: boolean;
  onClose: () => void;
  stepId: string;
  stepName: string;
  full_text: string;
  processMapId: number;
  projectId?: number;
  processId?: string;
  stepDbId?: number; // Database ID for API calls
  validationStatus?: 'ai_generated' | 'human_verified' | 'human_modified';
  isLocked?: boolean;
  onStatusUpdate?: () => void; // Callback to refresh parent component
}

// ✅ UPDATED: Interface to match real audit enhancement structure from AI
interface AuditData {
  step_id: string;
  step_name: string;
  has_enhanced_data: boolean;
  confidence_score?: number;
  stakeholder_assigned?: string;
  category_assigned?: string;
  processing_method?: string;
  created_at?: string;

  // Real audit enhancement data structure from AI
  reasoning_chains?: {
    id: number;
    step_id: string;
    reasoning_type: string;
    reasoning_content: {
      extraction_logic?: string;
      assignment_logic?: string;
      original_text?: string;
      final_text?: string;
      stakeholder_assigned?: string;
      category_determined?: string;
      processing_method?: string;
      quality_indicators?: {
        step_specificity: string;
        source_traceability: string;
        stakeholder_clarity: string;
      };
    };
    confidence_score: number;
    ai_model_used: string;
    created_at: string;
  }[];

  source_citations?: {
    id: number;
    step_id: string;
    document_id: number;
    document_name: string;
    citation_text: string;
    page_reference?: string;
    page_number?: number;
    section_reference?: string;
    confidence_score: number;
    created_at: string;
  }[];

  cross_validations?: {
    id: number;
    validation_type: string;
    validation_result: string;
    validation_notes?: string;
    validator: string;
    created_at: string;
  }[];

  reasoning_edits?: {
    id: number;
    edit_type: string;
    original_content: any;
    edited_content: any;
    editor_notes?: string;
    editor_id: string;
    created_at: string;
  }[];

  extraction_audit?: {
    document_extractions: {
      document_id: number;
      document_name: string;
      extracted_text: string;
      extraction_confidence: number;
      extraction_method: string;
    }[];
  };
}

const StepAuditPopup: React.FC<StepAuditPopupProps> = ({
  isOpen,
  onClose,
  stepId,
  stepName,
  full_text,
  processMapId,
  projectId,
  processId,
  stepDbId,
  validationStatus = 'ai_generated',
  isLocked = false,
  onStatusUpdate
}) => {

  const [auditData, setAuditData] = useState<AuditData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('edits');
  const [error, setError] = useState<string | null>(null);
  const [currentValidationStatus, setCurrentValidationStatus] = useState(validationStatus);
  const [currentLocked, setCurrentLocked] = useState(isLocked);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (isOpen && stepId && processMapId) {
      loadStepAuditData();
    }
  }, [isOpen, stepId, processMapId]);

  // Update validation status when props change
  useEffect(() => {
    setCurrentValidationStatus(validationStatus);
    setCurrentLocked(isLocked);
  }, [validationStatus, isLocked]);

  // Handler to mark step as verified
  const handleMarkAsVerified = async () => {
    if (!stepDbId) {
      toast.error('Step database ID not available');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('jarvis_access_token') || localStorage.getItem('authToken');

      await api.patch(
        `/api/analysis/process-map-steps/${stepDbId}/validation`,
        { validation_status: 'human_verified' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCurrentValidationStatus('human_verified');
      toast.success('✅ Step marked as verified');

      if (onStatusUpdate) onStatusUpdate();
    } catch (error: any) {
      // Silently handle expected 404 - step not synced to process_map_steps table yet
      if (error.response?.status !== 404) {
        console.error('Failed to mark as verified:', error);
      }
      const errorMsg = error.response?.status === 404
        ? 'Step not synced yet. This feature requires process map sync.'
        : (error.response?.data?.error || 'Failed to update verification status');
      toast.error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  // Handler to toggle lock status
  const handleToggleLock = async () => {
    if (!stepDbId) {
      toast.error('Step database ID not available');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('jarvis_access_token') || localStorage.getItem('authToken');
      const newLockedState = !currentLocked;

      await api.patch(
        `/api/analysis/process-map-steps/${stepDbId}/validation`,
        { locked: newLockedState },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCurrentLocked(newLockedState);
      toast.success(newLockedState ? '🔒 Step locked' : '🔓 Step unlocked');

      if (onStatusUpdate) onStatusUpdate();
    } catch (error: any) {
      // Silently handle expected 404 - step not synced to process_map_steps table yet
      if (error.response?.status !== 404) {
        console.error('Failed to toggle lock:', error);
      }
      const errorMsg = error.response?.status === 404
        ? 'Step not synced yet. This feature requires process map sync.'
        : (error.response?.data?.error || 'Failed to update lock status');
      toast.error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  const loadStepAuditData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('jarvis_access_token') || localStorage.getItem('authToken');

      // ✅ UPDATED: Use new endpoint that returns only real data - no fallbacks
      const response = await api.get(
        `/api/analysis/step-audit-data/${projectId}/${stepId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // A missing enhanced payload is now the normal case, not an error: the
      // audit trail is built on demand at export time rather than during map
      // generation. Edit history and the validation controls still render, and
      // the panel points the reviewer at Export -> Audit Trail.
      setAuditData(response.data || null);

    } catch (error: any) {
      if (error.response?.status === 404) {
        // Expected when the audit trail has not been exported/built yet.
        setAuditData(null);
      } else if (error.response?.status === 401) {
        setError('Authentication required. Please refresh the page.');
        toast.error('Failed to load step details');
      } else {
        console.error('Failed to load step audit data:', error);
        setError('Failed to load step details');
        toast.error('Failed to load step details');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 85) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getValidationResultColor = (result: string) => {
    switch (result) {
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'improved': return 'text-blue-600 bg-blue-100';
      case 'challenged': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // FIX: Better formatting for extraction logic + filter out stakeholder duplicates
  const formatExtractionLogic = (text: string, filterStakeholderInfo: boolean = false) => {
    if (!text) return text;

    let lines = text
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>')
      .replace(/•\s*/g, '\n• ')
      .replace(/^\s*-\s+/gm, '\n• ')
      .replace(/\s*•\s*/g, '\n• ')
      .replace(/\n\s*\n/g, '\n')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    // ✅ Filter out stakeholder-related lines if requested (to avoid duplication with green box)
    if (filterStakeholderInfo) {
      let skipUntilNextSection = false;
      lines = lines.filter(line => {
        const lowerLine = line.toLowerCase();

        // Check if this line starts a stakeholder section
        if (lowerLine.includes('stakeholder assignment') ||
            lowerLine.includes('assigned to') && lowerLine.includes('based on')) {
          skipUntilNextSection = true;
          return false;
        }

        // If we're in skip mode, check if we hit a new major section
        if (skipUntilNextSection) {
          // New section indicators (you can adjust these)
          if (lowerLine.includes('level 3 considerations') ||
              lowerLine.includes('operational level') ||
              lowerLine.includes('quality indicators') ||
              lowerLine.includes('source analysis') ||
              (!line.startsWith('•') && line.length > 0 && !lowerLine.includes('document') && !lowerLine.includes('role') && !lowerLine.includes('estimator'))) {
            skipUntilNextSection = false;
            return true;
          }
          return false;
        }

        return true;
      });
    }

    return lines
      .map(line => {
        if (line.startsWith('•')) {
          return `<div class="flex items-start mb-2"><span class="text-blue-600 mr-2 mt-0.5">•</span><span class="flex-1">${line.substring(1).trim()}</span></div>`;
        }
        return `<div class="mb-2">${line}</div>`;
      })
      .join('');
  };

  const renderReasoningChains = () => {
    if (!auditData?.reasoning_chains?.length) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Brain className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No reasoning chains available for this step</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {auditData.reasoning_chains.map((chain) => (
          <div key={chain.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center space-x-2">
                <Brain className="w-5 h-5 text-purple-500" />
                <span className="font-medium text-gray-900 capitalize">
                  {chain.reasoning_type.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(chain.confidence_score)}`}>
                  {chain.confidence_score}% confidence
                </span>
                <span className="text-xs text-gray-500">{chain.ai_model_used}</span>
              </div>
            </div>

            <div className="space-y-3">
              {/* Real Database Values Section */}
              {chain.reasoning_content.original_text && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Original Step Text:</h5>
                  <div className="text-sm text-gray-600 bg-yellow-50 rounded p-3 border-l-4 border-yellow-400">
                    {chain.reasoning_content.original_text}
                  </div>
                </div>
              )}

              {/* ❌ HIDDEN: Final Step Name - duplicate of Original Step Text
              {chain.reasoning_content.final_text && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Final Step Name:</h5>
                  <div className="text-sm text-gray-600 bg-blue-50 rounded p-3 border-l-4 border-blue-400">
                    {chain.reasoning_content.final_text}
                  </div>
                </div>
              )}
              */}

              {chain.reasoning_content.extraction_logic && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">AI Reasoning Chain:</h5>
                  <div className="text-sm text-gray-600 bg-blue-50 rounded p-3 border-l-4 border-blue-400">
                    <div
                      className="prose prose-sm max-w-none extraction-logic-content"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(formatExtractionLogic(chain.reasoning_content.extraction_logic, true))
                      }}
                    />
                  </div>
                </div>
              )}

              {chain.reasoning_content.assignment_logic && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Stakeholder Assignment Logic:</h5>
                  <div className="text-sm text-gray-600 bg-green-50 rounded p-3 border-l-4 border-green-400">
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(formatExtractionLogic(chain.reasoning_content.assignment_logic))
                      }}
                    />
                  </div>
                </div>
              )}

              {/* ✅ NEW: Source Citations section - moved from sidebar to AI Reasoning tab */}
              {auditData?.source_citations && auditData.source_citations.length > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">
                    Referenced Documents:
                  </h5>
                  <div className="text-sm text-gray-600 bg-purple-50 rounded p-3 border-l-4 border-purple-400">
                    <div className="space-y-3">
                      {auditData.source_citations.map((citation) => (
                        <div key={citation.id} className="bg-white rounded-lg p-3 border border-purple-200">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center space-x-2">
                              <FileText className="w-4 h-4 text-purple-600" />
                              <span className="font-medium text-sm text-purple-900">
                                {citation.document_name || `Document ID: ${citation.document_id || 'Unknown'}`}
                              </span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(citation.confidence_score)}`}>
                              {citation.confidence_score}% confidence
                            </span>
                          </div>

                          <div className="bg-purple-100 rounded p-2 mb-2">
                            <p className="text-xs text-purple-900 italic">
                              "{citation.citation_text}"
                            </p>
                          </div>

                          <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                            {citation.document_id && (
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                Doc ID: {citation.document_id}
                              </span>
                            )}
                            {citation.page_number && (
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                Page {citation.page_number}
                              </span>
                            )}
                            {citation.page_reference && (
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                Ref: {citation.page_reference}
                              </span>
                            )}
                            {citation.section_reference && (
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                Section: {citation.section_reference}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mt-3">
                {chain.reasoning_content.stakeholder_assigned && (
                  <div className="bg-gray-50 rounded p-2">
                    <h6 className="text-xs font-medium text-gray-600 mb-1">Assigned To (DB Field):</h6>
                    <p className="text-sm font-medium text-blue-600">
                      {chain.reasoning_content.stakeholder_assigned}
                    </p>
                  </div>
                )}

                {chain.reasoning_content.category_determined && (
                  <div className="bg-gray-50 rounded p-2">
                    <h6 className="text-xs font-medium text-gray-600 mb-1">Category (DB Field):</h6>
                    <p className="text-sm font-medium text-green-600 capitalize">
                      {chain.reasoning_content.category_determined}
                    </p>
                  </div>
                )}

                {/* {chain.reasoning_content.processing_method && (
                  <div className="bg-gray-50 rounded p-2">
                    <h6 className="text-xs font-medium text-gray-600 mb-1">Processing Method:</h6>
                    <p className="text-sm font-medium text-purple-600">
                      {chain.reasoning_content.processing_method}
                    </p>
                  </div>
                )} */}
              </div>
            </div>

            <div className="text-xs text-gray-500 flex items-center mt-3 pt-3 border-t border-gray-100">
              <Clock className="w-3 h-3 mr-1" />
              {formatDate(chain.created_at)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSourceCitations = () => {
    if (!auditData?.source_citations?.length) {
      return (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No source citations available for this step</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {auditData.source_citations.map((citation) => (
          <div key={citation.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-blue-500" />
                <span className="font-medium text-gray-900">
                  {citation.document_name || `Document ID: ${citation.document_id || 'Unknown'}`}
                </span>
                {/* <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  From audit_trail.step_traceability
                </span> */}
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(citation.confidence_score)}`}>
                {citation.confidence_score}% confidence
              </span>
            </div>

            <div className="bg-blue-50 rounded p-3 mb-2 border-l-4 border-blue-400">
              <p className="text-sm text-gray-700 italic">
                "{citation.citation_text}"
              </p>
            </div>

            <div className="flex justify-between items-center text-xs text-gray-500">
              <div className="flex items-center space-x-4">
                {citation.document_id && (
                  <span className="bg-gray-100 px-2 py-1 rounded">DB Document ID: {citation.document_id}</span>
                )}
                {citation.page_number && (
                  <span className="bg-gray-100 px-2 py-1 rounded">Page {citation.page_number}</span>
                )}
                {citation.page_reference && (
                  <span className="bg-gray-100 px-2 py-1 rounded">Page Ref: {citation.page_reference}</span>
                )}
                {citation.section_reference && (
                  <span className="bg-gray-100 px-2 py-1 rounded">Section: {citation.section_reference}</span>
                )}
              </div>
              <div className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                {formatDate(citation.created_at)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCrossValidations = () => {
    if (!auditData?.cross_validations?.length) {
      return (
        <div className="text-center py-8 text-gray-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No cross-validations available for this step</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {auditData.cross_validations.map((validation) => (
          <div key={validation.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center space-x-2">
                <Target className="w-5 h-5 text-green-500" />
                <span className="font-medium text-gray-900 capitalize">
                  {validation.validation_type.replace('_', ' ')}
                </span>
                {/* <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                  From audit_trail.edit_history
                </span> */}
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${getValidationResultColor(validation.validation_result)}`}>
                {validation.validation_result}
              </span>
            </div>

            {validation.validation_notes && (
              <div className="bg-gray-50 rounded p-3 mb-2">
                <div className="text-xs font-medium text-gray-600 mb-1">Validation Notes:</div>
                <p className="text-sm text-gray-700">
                  {validation.validation_notes}
                </p>
              </div>
            )}

            <div className="flex justify-between items-center text-xs text-gray-500">
              <div className="flex items-center space-x-3">
                <div className="flex items-center">
                  <User className="w-3 h-3 mr-1" />
                  <span>Validator: {validation.validator}</span>
                </div>
                {validation.id && (
                  <span className="bg-gray-100 px-2 py-1 rounded">DB Record ID: {validation.id}</span>
                )}
              </div>
              <div className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                {formatDate(validation.created_at)}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderReasoningEdits = () => {
    if (!auditData?.reasoning_edits?.length) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Edit3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No reasoning edits available for this step</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {auditData.reasoning_edits.map((edit) => (
          <div key={edit.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center space-x-2">
                <Edit3 className="w-5 h-5 text-orange-500" />
                <span className="font-medium text-gray-900 capitalize">
                  {edit.edit_type.replace('_', ' ')}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                <User className="w-3 h-3 inline mr-1" />
                {edit.editor_id}
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">Original:</h5>
                <div className="bg-red-50 rounded p-2 border-l-4 border-red-400">
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                    {typeof edit.original_content === 'string'
                      ? edit.original_content
                      : JSON.stringify(edit.original_content, null, 2)
                    }
                  </pre>
                </div>
              </div>

              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">Edited:</h5>
                <div className="bg-green-50 rounded p-2 border-l-4 border-green-400">
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                    {typeof edit.edited_content === 'string'
                      ? edit.edited_content
                      : JSON.stringify(edit.edited_content, null, 2)
                    }
                  </pre>
                </div>
              </div>
            </div>

            {edit.editor_notes && (
              <div className="mt-3">
                <h5 className="text-xs font-medium text-gray-700 mb-1">Editor Notes:</h5>
                <p className="text-sm text-gray-600 bg-yellow-50 rounded p-2">
                  {edit.editor_notes}
                </p>
              </div>
            )}

            <div className="text-xs text-gray-500 flex items-center mt-2">
              <Clock className="w-3 h-3 mr-1" />
              {formatDate(edit.created_at)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderExtractionAudit = () => {
    // First check if we have the detailed reasoning from AI Reasoning tab
    const detailedReasoning = auditData?.reasoning_chains?.[0]?.reasoning_content?.extraction_logic;

    if (!auditData?.extraction_audit && !detailedReasoning) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Database className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No extraction audit available for this step</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Detailed Extraction Logic */}
        {detailedReasoning && (
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <Brain className="w-4 h-4 mr-2" />
              AI Extraction Analysis
            </h4>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="prose prose-sm max-w-none">
                <div
                  className="text-sm text-gray-700 extraction-logic-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(formatExtractionLogic(detailedReasoning))
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Document Extractions - Real Database Data */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <Search className="w-4 h-4 mr-2" />
            Document Extractions (Database Values)
          </h4>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm text-blue-700 mb-2">
                  <strong>Current Step Text (full_text prop)</strong>
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    From frontend display
                  </span>
                </div>
                <div className="text-sm text-blue-700 bg-white p-2 rounded border">
                  {full_text}
                </div>
                
                {/* Show original audit trail text if different */}
                {auditData?.reasoning_chains?.[0]?.reasoning_content?.original_text && 
                 auditData.reasoning_chains[0].reasoning_content.original_text !== full_text && (
                  <div className="mt-3">
                    <div className="text-sm text-green-700 mb-2">
                      <strong>Original Text from audit_trail.step_traceability</strong>
                      <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        From database audit_trail
                      </span>
                    </div>
                    <div className="text-sm text-green-700 bg-white p-2 rounded border">
                      {auditData.reasoning_chains[0].reasoning_content.original_text}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Show source documents if available */}
          {auditData?.source_citations && auditData.source_citations.length > 0 && (
            <div className="mt-4 space-y-2">
              <h5 className="text-sm font-medium text-gray-800">Referenced Documents:</h5>
              {auditData.source_citations.map((citation, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm text-blue-700">
                        {citation.document_name}
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(citation.confidence_score)}`}>
                      {citation.confidence_score}% confidence
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded p-2">
                    <p className="text-xs text-gray-700 italic">
                      "{citation.citation_text}"
                    </p>
                  </div>
                  {citation.page_reference && (
                    <div className="text-xs text-gray-500 mt-1">
                      Page: {citation.page_reference}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Real Database Processing Steps */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3 flex items-center">
            <Database className="w-4 h-4 mr-2" />
            Database Processing Information
          </h4>

          <div className="space-y-3">
            {/* Actual Audit Trail Data */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <span className="text-sm font-medium">Database Field Values</span>
                </div>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  From audit_trail.step_traceability
                </span>
              </div>
              <div className="ml-8 space-y-2">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="p-2 bg-yellow-50 rounded">
                    <strong>Step ID:</strong> {stepId}
                  </div>
                  <div className="p-2 bg-blue-50 rounded">
                    <strong>Process Map ID:</strong> {processMapId}
                  </div>
                  <div className="p-2 bg-green-50 rounded">
                    <strong>AI Model:</strong> {auditData?.reasoning_chains?.[0]?.ai_model_used || 'Not specified'}
                  </div>
                  <div className="p-2 bg-purple-50 rounded">
                    <strong>Confidence Score:</strong> {auditData?.reasoning_chains?.[0]?.confidence_score || 0}%
                  </div>
                </div>
              </div>
            </div>

            {/* Stakeholder Assignment from DB */}
            {auditData?.reasoning_chains?.[0]?.reasoning_content?.stakeholder_assigned && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      2
                    </div>
                    <span className="text-sm font-medium">Stakeholder Assignment </span>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    stakeholder_assigned field
                  </span>
                </div>
                <div className="text-sm text-gray-600 ml-8">
                  <strong>Assigned to:</strong> {auditData.reasoning_chains[0].reasoning_content.stakeholder_assigned}
                </div>
                {auditData.reasoning_chains[0].reasoning_content.assignment_logic && (
                  <div className="ml-8 mt-2 p-2 bg-green-50 rounded text-xs">
                    <strong>Assignment Logic:</strong> {auditData.reasoning_chains[0].reasoning_content.assignment_logic}
                  </div>
                )}
              </div>
            )}

            {/* Category Assignment from DB */}
            {auditData?.reasoning_chains?.[0]?.reasoning_content?.category_determined && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      3
                    </div>
                    <span className="text-sm font-medium">Category Classification </span>
                  </div>
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                    category_assigned field
                  </span>
                </div>
                <div className="text-sm text-gray-600 ml-8">
                  <strong>Category:</strong> {auditData.reasoning_chains[0].reasoning_content.category_determined}
                </div>
              </div>
            )}

            {/* Source Documents from DB */}
            {auditData?.source_citations && auditData.source_citations.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                      4
                    </div>
                    <span className="text-sm font-medium">Source Traceability </span>
                  </div>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    source_documents field
                  </span>
                </div>
                <div className="text-sm text-gray-600 ml-8">
                  <strong>Source Documents:</strong> {auditData.source_citations.length} document(s) referenced
                </div>
                <div className="ml-8 mt-2 text-xs space-y-1">
                  {auditData.source_citations.slice(0, 3).map((citation, index) => (
                    <div key={index} className="p-2 bg-purple-50 rounded">
                      {citation.document_name || `Document ID: ${citation.document_id}`}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Database Metadata */}
        {auditData?.reasoning_chains?.[0] && (
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <BarChart3 className="w-4 h-4 mr-2" />
              Database Metadata (Real Values)
            </h4>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong className="text-gray-800">Processing Method (DB Field):</strong>
                  <div className="mt-1 p-2 bg-white rounded border">
                    {auditData.reasoning_chains[0].reasoning_content.processing_method || 'Not specified in audit_trail'}
                  </div>
                </div>
                <div>
                  <strong className="text-gray-800">AI Model Used (DB Field):</strong>
                  <div className="mt-1 p-2 bg-white rounded border">
                    {auditData.reasoning_chains[0].ai_model_used || 'Not specified in audit_trail'}
                  </div>
                </div>
                <div>
                  <strong className="text-gray-800">Reasoning Type (DB Field):</strong>
                  <div className="mt-1 p-2 bg-white rounded border">
                    {auditData.reasoning_chains[0].reasoning_type || 'Not specified'}
                  </div>
                </div>
                <div>
                  <strong className="text-gray-800">Created At (DB Field):</strong>
                  <div className="mt-1 p-2 bg-white rounded border">
                    {formatDate(auditData.reasoning_chains[0].created_at)}
                  </div>
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-600 bg-blue-50 rounded p-2">
                <strong>Note:</strong> All data displayed above is pulled directly from the database audit_trail.step_traceability table. 
                No hardcoded or fallback values are used - only real stored audit information.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Audit trail is export-only. The AI Reasoning and Cross Validations tabs were
  // removed from this modal: their payload costs ~1 LLM call per 3 steps plus the
  // exception pass, and it is a compliance record rather than something a reviewer
  // acts on mid-review. It now ships via Export -> Audit Trail (PDF/CSV), built on
  // demand. What stays here is what a reviewer actually uses in the moment:
  // validation status, verify/lock, and the human edit history.
  const tabs = [
    { id: 'edits', label: 'Edit History', icon: Edit3, count: auditData?.reasoning_edits?.length || 0 },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-semibold text-gray-900">Step Details</h2>

              {/* Verification Controls */}
              <div className="flex items-center space-x-2">
                {/* Validation Status Badge */}
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                  currentValidationStatus === 'human_verified'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : currentValidationStatus === 'human_modified'
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300'
                }`}>
                  {currentValidationStatus === 'human_verified' ? '✓ Verified' :
                   currentValidationStatus === 'human_modified' ? '✎ Modified' :
                   '⚡ AI Generated'}
                </span>

                {/* Lock Status Badge */}
                {currentLocked && (
                  <span className="text-xs px-3 py-1 rounded-full font-medium bg-yellow-100 text-yellow-700 border border-yellow-300 flex items-center">
                    <Lock className="w-3 h-3 mr-1" />
                    Locked
                  </span>
                )}

                {/* Mark as Verified Button */}
                {stepDbId && currentValidationStatus !== 'human_verified' && (
                  <button
                    onClick={handleMarkAsVerified}
                    disabled={updating}
                    className="flex items-center space-x-1 px-3 py-1.5 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Shield className="w-4 h-4" />
                    <span>{updating ? 'Updating...' : 'Mark as Verified'}</span>
                  </button>
                )}

                {/* Toggle Lock Button */}
                {stepDbId && (
                  <button
                    onClick={handleToggleLock}
                    disabled={updating}
                    className={`flex items-center space-x-1 px-3 py-1.5 text-sm rounded-md transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${
                      currentLocked
                        ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                        : 'bg-gray-500 text-white hover:bg-gray-600'
                    }`}
                  >
                    {currentLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    <span>{updating ? 'Updating...' : currentLocked ? 'Unlock' : 'Lock'}</span>
                  </button>
                )}
              </div>
            </div>

            <p className="text-gray-600 mt-1 max-w-2xl">{full_text}</p>

            {/* Data Source Indicator */}
            <div className="mt-3 flex items-center space-x-2">
              {auditData && ((auditData.reasoning_chains?.length || 0) > 0 || (auditData.source_citations?.length || 0) > 0) ? (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-700 font-medium">Audit Data Available</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-red-700 font-medium">No Audit Data in Database</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 ml-4"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar Tabs */}
          <div className="w-64 border-r border-gray-200 bg-gray-50">
            <nav className="space-y-1 p-4">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <div className="flex items-center">
                      <Icon className="w-4 h-4 mr-2" />
                      {tab.label}
                    </div>
                    {tab.count > 0 && (
                      <span className={`text-xs px-2 py-1 rounded-full ${activeTab === tab.id ? 'bg-blue-200 text-blue-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-600 mb-2">{error}</p>
                  <button
                    onClick={loadStepAuditData}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {renderReasoningEdits()}

                <div className="mt-6 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                  <p className="text-xs leading-relaxed text-gray-600">
                    <span className="font-medium text-gray-700">Looking for the AI reasoning and source citations?</span>{' '}
                    The full audit trail — per-step reasoning chains, source citations, and
                    cross-validations for every step in this map — is available as a downloadable
                    record under <span className="font-medium">Export → Audit Trail</span> in the
                    validation bar. It is generated on demand so it does not slow down map generation.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        {/* <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              Step ID: <code className="bg-gray-200 px-1 rounded">{stepId}</code>
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div> */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex justify-between items-start">
            <div className="text-sm text-gray-500 space-y-1 flex-1">
              <p>Frontend Step ID: <code className="bg-gray-200 px-1 rounded">{stepId}</code></p>
              <p>Process Map ID: <code className="bg-blue-100 px-1 rounded text-blue-800">{processMapId}</code></p>
              {auditData && (
                <div className="mt-2 text-xs">
                  <p className="font-medium text-gray-600">Available Data:</p>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <span className={`px-2 py-1 rounded ${(auditData.reasoning_chains?.length || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      Reasoning: {auditData.reasoning_chains?.length || 0}
                    </span>
                    <span className={`px-2 py-1 rounded ${(auditData.source_citations?.length || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      Citations: {auditData.source_citations?.length || 0}
                    </span>
                    <span className={`px-2 py-1 rounded ${(auditData.cross_validations?.length || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      Validations: {auditData.cross_validations?.length || 0}
                    </span>
                    <span className={`px-2 py-1 rounded ${(auditData.reasoning_edits?.length || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      Edits: {auditData.reasoning_edits?.length || 0}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors ml-4"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepAuditPopup;